import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { compose } from 'redux';
import { useRouter } from 'next/router';
import { SocialProfileJsonLd } from 'next-seo';
import { createStructuredSelector } from 'reselect';
import { get, last, find } from 'lodash';
import { makeStyles } from '@material-ui/core/styles';
import { withTranslation, Router, i18n } from '../../src/i18n';
import { getFullProfileTitle, getRoutePartWithSlug, getRouteWithSlug, buildSocialLinks } from '../../utils/common';
import StubContent from '../../components/StubContent';
import StubLayout from '../../components/StubLayout';
import ArtistInfoCard from '../../components/ArtistInfoCard';
import ArtistSidebar from '../../components/ArtistSidebar';
import { TP, ARTIST_TAB_VALUES, PAGE_NAMES } from '../../constants/consts';
import ArtistImages from '../../components/ArtistViewsComponents/ImagesView';
import ArtistVideos from '../../components/ArtistViewsComponents/VideosView';
import ArtistReviews from '../../components/ArtistViewsComponents/ReviewsView';
import { StubPagePerformances } from '../StubPagePerformances';

import {
  selectChosenProfileData,
  selectChosenProfileReviews,
  selectChosenProfileBio,
  selectChosenProfileAgreements,
  selectVideo,
  selectImages,
  selectBannerImages,
  selectBannerLoading,
  selectChosenProfileDisplayRepertoire,
} from './selectors';

import route from '../../constants/routes';
import { selectIsLoggedInState } from '../../store/selectors';
import ArtistRepertoireView from '../../components/ArtistRepertoireView';
import StubPageSideCard from '../../components/StubPageSideCard';
import ContactCard from '../../components/ContactCard';
import ArtistAgency from '../../components/ArtistAgency';
// import getArtistSeo from './seo';

const baseURL = process.env.FRONTBASE_URL;

const useStyles = makeStyles(theme => ({
  toolBar: {
    padding: '16px 32px 2px',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    [theme.breakpoints.down('sm')]: {
      height: 65,
      padding: '0 15px 15px',
    },
  },
  searchField: {
    width: '30%',
    maxWidth: 275,
    [theme.breakpoints.down('sm')]: {
      width: '50%',
      maxWidth: '100%',
    },
  },
  profileSection: {
    display: 'flex',
    [theme.breakpoints.down('sm')]: {
      flexDirection: 'column',
    },
  },
  infoBlock: {
    flexGrow: 1,
  },
  artistImage: {
    maxWidth: '645px',
    maxHeight: '673px',
    objectFit: 'contain',
    objectPosition: 'top',
    [theme.breakpoints.down('sm')]: {
      minWidth: '100%',
      maxHeight: 310,
      objectFit: 'contain',
    },
  },
  tabs: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    scrollBehavior: 'smooth',
    borderBottom: '1px solid #8b8b8b',
    zIndex: 0,
  },
  reviewsCard: {
    padding: 0,
  },
  classNameContent: {
    paddingLeft: 0,
    paddingTop: 0,
  },
  artistCard: {
    padding: '0 !important',
  },
  contactCard: {
    [theme.breakpoints.up('md')]: {
      marginTop: 40,
    },
  },
}));

export function Artist({
  t,
  profile,
  repertoire,
  photos,
  videos,
  isLoggedIn,
  reviews,
  activeTab,
  bioData,
  agreementData,
  lang,
  bannerImage,
  bannerLoading,
  performances,
  queryValues,
}) {
  const classes = useStyles();
  const router = useRouter();
  const { contacts, agencies } = profile;
  const profileId = last(get(router, 'query.id', '').split('-'));
  const bio = get(bioData, 'data', []);
  const language = typeof window === 'undefined' ? lang : i18n.language;
  const artistBio = find(bio, { language: { lang: language } }) || find(bio, { language: { lang: 'en' } });

  const socialLinks = contacts ? buildSocialLinks(contacts)?.map(item => item.link) : [];
  const imageLinks = photos ? photos?.data?.map(item => item.file?.urls?.medium) : [];

  const goToTab = val => {
    const path = getRouteWithSlug(route.ARTISTS, profile);
    const finalPath = val === ARTIST_TAB_VALUES.HOME ? path : `${path}/${val}`;
    Router.push(`${route.ARTISTS}/[id]/[[...action]]`, finalPath, {
      shallow: true,
    });
  };

  const homeSidebarContent = (reviews?.data?.length > 0 ||
    photos?.data?.length > 0 ||
    videos?.data?.length > 0 ||
    repertoire?.total > 0 ||
    contacts?.length > 0 ||
    agencies?.length > 0 ||
    artistBio?.content ||
    profile?.description) && (
    <ArtistSidebar
      photos={photos?.data || []}
      profile={profile}
      videos={videos?.data || []}
      repertoire={repertoire}
      isLoggedIn={isLoggedIn}
      reviews={reviews}
      bio={bio}
      artistBio={artistBio}
      agreementData={agreementData}
      goToTab={goToTab}
      lang={language}
    />
  );

  const scheduleLinksProps = {
    href: `${route.ARTISTS}/[id]/[[...action]]`,
    as: `${getRouteWithSlug(route.ARTISTS, profile)}/${ARTIST_TAB_VALUES.PERFORMANCES}`,
    shallow: true,
  };

  const scheduleContent = (tab, showSubFilters) => (
    <StubPagePerformances
      tab={tab}
      t={t}
      page={PAGE_NAMES.ARTIST}
      isLoggedIn={isLoggedIn}
      entityId={profileId}
      profile={profile}
      className={classes}
      linkProps={scheduleLinksProps}
      initialPerformances={performances}
      showSubFilters={showSubFilters}
      queryValues={queryValues}
    />
  );

  const artistTabs = [
    {
      name: t(`${TP}.m_HOME`),
      value: 'home',
      isShown: true,
      mainContent: scheduleContent('home', false),
      sidebarContent: homeSidebarContent,
    },
    {
      name: t(`${TP}.m_PERF`),
      value: 'performances',
      isShown: true,
      mainContent: scheduleContent('schedule', true),
    },
    {
      name: t(`${TP}.m_LISTREPERTOIRE`),
      value: 'repertoire',
      isShown: repertoire?.total > 0,
      mainContent: (
        <ArtistRepertoireView
          all={repertoire?.all}
          future={repertoire?.future}
          retired={repertoire?.retired}
          active={repertoire?.active}
          profile={profile}
        />
      ),
    },
    {
      name: t(`${TP}.m_BIO`),
      value: 'bio',
      isShown: artistBio?.content || profile?.description,
      mainContent: (
        <StubPageSideCard classNameContent={classes.classNameContent} title={t(`${TP}.m_ARTIST_BIOGRAPHY`)}>
          <ArtistInfoCard
            className={classes.artistCard}
            isLoggedIn={isLoggedIn}
            artistBio={artistBio}
            bio={bio}
            profile={profile}
            isBio
            lang={language}
          ></ArtistInfoCard>
        </StubPageSideCard>
      ),
    },
    {
      name: t(`${TP}.m_ARTIST_VIDEOS`),
      value: 'videos',
      isShown: videos?.data?.length > 0,
      mainContent: <ArtistVideos t={t} videos={videos} />,
    },
    {
      name: t(`${TP}.m_IMAGES`),
      value: 'images',
      isShown: photos?.data?.length > 0,
      mainContent: <ArtistImages t={t} photos={photos} />,
    },
    {
      name: t(`${TP}.m_ARTICLE`),
      value: 'reviews',
      isShown: reviews?.data?.length > 0,
      mainContent: (
        <StubPageSideCard title={t(`${TP}.m_ARTICLE`)} classNameContent={classes.reviewsCard}>
          <ArtistReviews reviews={reviews} />
        </StubPageSideCard>
      ),
    },
    {
      name: t(`${TP}.m_CONTACT`),
      value: 'contact',
      isShown: get(profile, 'agencies', []).length > 0 || get(profile, 'contacts', []).length > 0,
      mainContent: <ArtistAgency agreementData={agreementData} />,
      sidebarContent: get(profile, 'contacts', []).length > 0 && (
        <ContactCard
          t={t}
          contacts={get(profile, 'contacts', [])}
          className={classes.contactCard}
          hideInfo={!isLoggedIn}
          subTitle={t(`${TP}.m_ACCOUNT_TYPES_ARTIST`)}
          socialLinksTitle={t(`${TP}.FOLLOW_ARTIST_ON`)}
        />
      ),
    },
  ];

  const title = getFullProfileTitle(profile);
  const customBreadcrumbs = [
    { path: route.HOME, title: `${TP}.m_HOME` },
    { path: route.ARTISTS, title: `${TP}.m_ARTISTS` },
    {
      path: getRoutePartWithSlug(profile),
      title,
    },
  ];

  const infoCard = () => (
    <>
      <ArtistInfoCard bio={artistBio} profile={profile} maxLines={2} lang={language}></ArtistInfoCard>
      <SocialProfileJsonLd
        type="Person"
        name={get(profile, 'name')}
        url={`${baseURL.replace(/\/$/, '')}`}
        sameAs={[...socialLinks]}
        image={[...imageLinks]}
      />
    </>
  );
  const stubContent = () => (
    <StubContent
      InfoCard={infoCard}
      onChange={goToTab}
      tabs={artistTabs.filter(tab => tab.isShown)}
      selectedTab={activeTab}
    />
  );

  // const seo = getArtistSeo(t, profile, performances);

  return (
    <StubLayout
      t={t}
      customBreadcrumbs={customBreadcrumbs}
      title={title}
      Content={stubContent}
      isLoggedIn={isLoggedIn}
      media={bannerImage?.data?.length > 0 && bannerImage.data[0]?.file?.urls?.large}
      bannerLoading={bannerLoading}
      // seo={seo}
    />
  );
}

const mapStateToProps = createStructuredSelector({
  profile: selectChosenProfileData(),
  repertoire: selectChosenProfileDisplayRepertoire(),
  reviews: selectChosenProfileReviews(),
  photos: selectImages(),
  videos: selectVideo(),
  isLoggedIn: selectIsLoggedInState(),
  bioData: selectChosenProfileBio(),
  agreementData: selectChosenProfileAgreements(),
  bannerImage: selectBannerImages(),
  bannerLoading: selectBannerLoading(),
});

const withConnect = connect(mapStateToProps, null);

Artist.propTypes = {
  t: PropTypes.func.isRequired,
  profile: PropTypes.object,
  repertoire: PropTypes.object,
  photos: PropTypes.object,
  videos: PropTypes.object,
  bioData: PropTypes.object,
  lang: PropTypes.string,
  bannerImage: PropTypes.object,
};

Artist.defaultProps = {
  profile: {},
  videos: [],
  repertoire: { total: 0, all: [], future: [], retired: [], active: [], short: { all: [], future: [] } },
};

export default compose(withConnect, withTranslation())(Artist);
